import React, { useEffect, useMemo, useRef, useState } from "react"
import { Grid, Typography, Tooltip, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faUser, faCircleEuro, faTrash, faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { faLock } from "@fortawesome/pro-solid-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { MeetingServiceOrdersResponse } from "../../../../../merchant/merchant/production/meetingService/meetingServiceApi"
import {
    DeliveryType,
    getDeliveryTypeValue,
    MeetingServiceOrder,
} from "../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { useExportMeetingServiceOrdersToCsv } from "../../../../../merchant/merchant/production/meetingService/components/useExportMeetingServiceOrdersToCsv"
import { fetchMeetingServiceForUser, UpcomingOrdersMeetingService } from "../meetingServiceApi"
import {
    DatePeriod,
    getTimePeriod,
} from "../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { useDateTime } from "../../../../../localization/useDateTime"
import { MeetingCardMenu } from "../../../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { MenuMeeting } from "../../../home/weekMenuTypes"
import { SimpleStatusChip } from "../../../../../merchant/merchant/production/meetingService/components/StatusMenu"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { useQueryClient } from "@tanstack/react-query"

const useStyles = makeStyles()((theme) => ({
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    secondLine: {
        color: theme.palette.tailwind.neutral[500],
    },
    classNameOverride: {
        paddingTop: "4rem",
        paddingBottom: "4rem",
    },
    classNameOverrideMultipleOrders: {
        paddingBottom: "3.5rem",
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

export function UserMeetingService() {
    const { classes } = useStyles()
    const todayDate = DateTime.now()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const todayStart = dateTimeFactory.now().startOf("day")
    const queryClient = useQueryClient()

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()

    const [datePeriod, setDatePeriod] = useState<DatePeriod>(DatePeriod.ALL)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [showSuccessDialog, setShowSuccessDialog] = useState<{
        open: boolean
        type?: SuccessDialogType
        ordersCount?: number
    }>({ open: false })

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate < todayStart ? todayStart : startDate,
        endDate: endDate && endDate < todayStart ? todayStart : endDate,
    })

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    const meetingServiceResult = useApiQuery(fetchMeetingServiceForUser, {
        queryName: UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE,
        dependencies: {
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate?.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate?.toISODate() : undefined,
        },
        select: selectMeetingService,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails",
                Cell: ({ row }) => {
                    const deliveryType = getDeliveryTypeValue(row.original.deliveryDetails.type)
                    return (
                        <Grid container gap={1.25}>
                            <Grid container alignItems="center" gap={2} flexWrap="nowrap">
                                <Typography variant="caption">{row.original.meetingDetails.meetingName}</Typography>
                                {row.original.paymentDetails?.isContantPayment && (
                                    <FontAwesomeIcon icon={faCircleEuro} color={"#334155"} />
                                )}
                                <Typography variant="body2" className={classes.wrapIconText}>
                                    <FontAwesomeIcon icon={faUser} color={"#334155"} />
                                    {row.original.deliveryDetails.sittings}
                                </Typography>
                                {row.original.deliveryDetails.isLocked && (
                                    <LocalizedStrict
                                        id="meeting-service-table-lock-column-locked"
                                        attrs={{ title: true }}
                                    >
                                        <Tooltip
                                            arrow
                                            title="The order has been locked by the merchant. Contact merchant for updates."
                                        >
                                            <FontAwesomeIcon icon={faLock} color={"#334155"} />
                                        </Tooltip>
                                    </LocalizedStrict>
                                )}
                            </Grid>

                            {deliveryType !== DeliveryType.DELIVERY ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    gap={1.5}
                                    flexWrap="nowrap"
                                    className={classes.secondLine}
                                >
                                    <Typography variant="caption">{deliveryType}</Typography>
                                </Grid>
                            ) : row.original.deliveryDetails.location.name ? (
                                <LocalizedStrict
                                    id="meeting-service-table-meeting"
                                    vars={{ locationName: row.original.deliveryDetails.location.name }}
                                >
                                    <Typography variant="caption" className={classes.secondLine}>
                                        Delivery to: {row.original.deliveryDetails.location.name}
                                    </Typography>
                                </LocalizedStrict>
                            ) : null}
                        </Grid>
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("meeting-service-table-amount-column"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("meeting-service-table-status-column"),
                Cell: ({ row }) => {
                    const statusDetails = row.original.deliveryDetails.statusDetails
                    return (
                        <Stack alignItems="center">
                            <SimpleStatusChip status={statusDetails.status} tooltipLabel={statusDetails.message} />
                        </Stack>
                    )
                },
                filterSelectOptions: [
                    {
                        value: "yes",
                        text: l10n.getString("meeting-service-table-lock-column-active-filter-yes"),
                    },
                    {
                        value: "no",
                        text: l10n.getString("meeting-service-table-lock-column-inactive-filter-no"),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const exportOrders = useExportMeetingServiceOrdersToCsv({
        data: selectedRows,
        startDate: isDatePicker ? (dateState.startDate ?? todayDate) : getTimePeriod(dateTimeFactory, datePeriod).start,
        endDate: dateState.endDate ?? todayDate,
    })

    const mapMeetingServiceToMeeting = (booking: MeetingServiceOrder): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            deliveryDetails: booking.deliveryDetails,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                onRowSelectionChange={setRowSelection}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MeetingCardMenu
                        ignoreMenuContainer
                        menuOnApi
                        meetings={meetingServiceResult.data}
                        meeting={mapMeetingServiceToMeeting(row.original)}
                        handleMenuClose={closeMenu}
                        variant="meeting-service"
                    />,
                ]}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <TimeOrDatePicker
                            isDatePicker={isDatePicker}
                            dateState={dateState}
                            setDateState={setDateState}
                            timePeriod={datePeriod}
                            handleChangeTimePeriod={setDatePeriod}
                            handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                        />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={meetingServiceResult.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>

                        <TableTotalAmountBox
                            data={table.getFilteredRowModel().rows}
                            isLoading={meetingServiceResult.isLoading}
                            accessor={(row) => row.original.amount}
                        />
                    </TableSummaryWrap>
                )}
                state={{
                    rowSelection,
                }}
            />

            {showSuccessDialog.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessDialog.type}
                    open={showSuccessDialog.open}
                    ordersCount={showSuccessDialog.ordersCount}
                    onClose={async () => {
                        setShowSuccessDialog({ open: false })
                        await queryClient.invalidateQueries({
                            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
                        })
                    }}
                />
            )}
        </>
    )
}
